import {
  Avatar,
  Box,
  Group,
  Menu,
  MantineColorScheme,
  SegmentedControl,
  Text,
  UnstyledButton,
  rem,
  useMantineColorScheme,
} from '@mantine/core';

import { IconLogout, IconUserCircle } from '@tabler/icons-react';
import { clearIssuer, isPersonalTenant, setSkipSessionProbe, UserInfo } from '@/lib/auth';
import { LogoutOptions } from '@auth0/auth0-react';
import { Tenant } from '@/clients/types';
import { useNavigate } from '@tanstack/react-router';

type Props = {
  logout: (options?: LogoutOptions) => Promise<void>;
  userInfo: UserInfo;
  tenant?: Tenant;
};

export function UserMenu({ logout, userInfo, tenant }: Props) {
  const navigate = useNavigate({ from: '/' });
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const handleLogout = async () => {
    setSkipSessionProbe();
    clearIssuer();
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };
  const iconStyle = { width: rem(18), height: rem(18) };
  // name クレームを持たない Auth0 アカウントでは auth.ts が空文字に落とすので、email で補う
  const displayName = userInfo?.name || userInfo?.email || '';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <Menu shadow="md" width={240} trigger="click-hover" zIndex={1100}>
      <Menu.Target>
        <UnstyledButton
          // md 以上では displayName が見えているので、読み上げ名にもそれを含める
          aria-label={`${displayName} のメニュー`}
          p={6}
          style={{ borderRadius: rem(22) }}
          miw={44}
          mih={44}
        >
          <Group gap="xs" wrap="nowrap">
            <Avatar src={userInfo?.picture || null} alt={displayName} radius="xl" size={32}>
              {initial}
            </Avatar>
            <Text visibleFrom="md" size="sm" lineClamp={1}>
              {displayName}
            </Text>
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {/* 押せる項目ではないので menuitem にしない（キーボードで止まってしまう） */}
        <Box px="xs" py={6}>
          <Text size="sm">{userInfo?.email}</Text>
          {!isPersonalTenant(userInfo) && (
            <Text size="xs" c="dimmed">
              テナント: {tenant?.spec.name ?? '...'}
            </Text>
          )}
        </Box>
        <Menu.Label>テーマ</Menu.Label>
        <Box px="xs" pb={6}>
          <SegmentedControl
            fullWidth
            size="xs"
            aria-label="テーマ"
            value={colorScheme}
            onChange={(value) => setColorScheme(value as MantineColorScheme)}
            data={[
              { value: 'auto', label: 'システム' },
              { value: 'light', label: 'ライト' },
              { value: 'dark', label: 'ダーク' },
            ]}
          />
        </Box>

        <Menu.Item
          onClick={() => navigate({ to: '/profile' })}
          leftSection={<IconUserCircle style={iconStyle} />}
        >
          プロファイル
        </Menu.Item>

        <Menu.Item
          onClick={handleLogout}
          leftSection={<IconLogout style={iconStyle} onClick={handleLogout} />}
        >
          ログアウト
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
